/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { isCrashingOutEnabledForUser } from '@/lib/featureFlags';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import {
  useGetMatterDetailsQuery,
  useMarkRetainerSignedMutation,
} from '@/services/api/mattersService';
import { CoverageCategory } from '@/services/types/coverage-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { ReviewGroupCoveragesModal } from './companion/ReviewGroupCoveragesModal';
import { CoverageAlertBanner } from './shared/CoverageAlertBanner';
import { CoverageDetails } from './coverage-details/CoverageDetails';
import { CoverageContent } from './CoverageContent';
import { CoverageOverviewHeader } from './CoverageOverviewHeader';
import { CoveragePageCrashoutPane } from './CoveragePageCrashoutPane';

interface CoverageOverviewPageProps {
  matterId: string;
}

export const CoverageOverviewPage = ({
  matterId,
}: CoverageOverviewPageProps) => {
  const { coverage_id } = useParams<{ coverage_id: string }>();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCoverageId, setSelectedCoverageId] = useState<string | null>(
    coverage_id || null,
  );
  const [filterCategory, setFilterCategory] = useState<
    CoverageCategory | undefined
  >(undefined);
  const [selectionMode, setSelectionMode] = useState(false);
  const [isManageSharedOpen, setIsManageSharedOpen] = useState(false);
  const [crashoutMode, setCrashoutMode] = useState(false);

  const user = useSelector(selectUser);
  const showCrashoutToggle = isCrashingOutEnabledForUser(user?.email);

  // Update selected coverage when URL parameter changes
  useEffect(() => {
    setSelectedCoverageId(coverage_id || null);
  }, [coverage_id]);

  const {
    data: coverages,
    isLoading,
    error,
    refetch,
  } = useGetCoveragesQuery({ matterId, category: filterCategory, includeShared: true });
  const { data: matterDetails } = useGetMatterDetailsQuery(matterId);
  const [markRetainerSigned, { isLoading: isMarking }] =
    useMarkRetainerSignedMutation();

  // Calculate status counts for triage display
  // Removed pending approval chip per design alignment; previously counted
  // coverages with status === NEEDS_APPROVAL.
  const selectedCoverage = coverages?.find((c) => c.id === selectedCoverageId);

  // Get the back parameter to determine where to navigate back to
  const backParam = searchParams.get('back');

  // Restore scroll to originating coverage card if present
  useEffect(() => {
    const highlightId = searchParams.get('coverageId');
    if (!highlightId) return;
    const el = document.querySelector(
      `[data-coverage-step-row="${highlightId}"]`,
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverages]);

  // Navigation handlers
  const handleCoverageSelect = (coverageId: string) => {
    setSelectedCoverageId(coverageId);
    setCrashoutMode(false);
    // Preserve the back parameter when navigating to coverage details
    const backQuery = backParam ? `?back=${backParam}` : '';
    navigate(`/matters/${matterId}/coverage/${coverageId}${backQuery}`);
  };

  const handleBackToList = () => {
    setSelectedCoverageId(null);
    navigate(`/matters/${matterId}/coverage`);
  };

  const handleBackToCoverage = () => {
    navigate(`/matters/${matterId}/coverage`);
  };

  const handleCreateCoverage = () => {
    navigate(`/matters/${matterId}/coverage/create`);
  };

  const handleDebugMentions = () => {
    navigate(`/matters/${matterId}/coverage/mentions-debug`);
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
  };

  // Show detail view if coverage is selected
  if (selectedCoverageId && selectedCoverage) {
    // Determine which back handler to use based on the back parameter
    const onBack =
      backParam === 'case-setup' ? handleBackToCoverage : handleBackToList;
    const backButtonText =
      backParam === 'case-setup' ? 'Back to Coverage' : 'Back to List';

    return (
      <div className="bg-gray-50 h-full overflow-hidden flex flex-col">
        <CoverageDetails
          matterId={matterId}
          coverage={selectedCoverage}
          onBack={onBack}
          onUpdate={refetch}
          backButtonText={backButtonText}
        />
      </div>
    );
  }

  // Show list view
  return (
    <div className="bg-white h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Header - now part of scrollable content */}
        <CoverageOverviewHeader
          onCreateCoverage={handleCreateCoverage}
          onDebugMentions={handleDebugMentions}
          matterId={matterId}
          onManageSharedCoverages={() => setIsManageSharedOpen(true)}
          showCrashoutToggle={showCrashoutToggle}
          crashoutMode={crashoutMode}
          onToggleCrashout={() => setCrashoutMode((prev) => !prev)}
        />

        {/* Retainer warning */}
        {!matterDetails?.retainer_signed && (
          <CoverageAlertBanner
            className="mt-4"
            message="Retainer is not signed. Confirm retainer is signed before working on coverage."
            actionLabel={isMarking ? 'Marking…' : 'Mark Signed'}
            disabled={isMarking}
            onAction={async () => {
              try {
                await markRetainerSigned({ matter_id: matterId }).unwrap();
                toast.success('Retainer marked as signed');
              } catch {
                toast.error('Failed to mark retainer as signed');
              }
            }}
          />
        )}

        {/* Content */}
        <div className="mt-6">
          {crashoutMode && coverages ? (
            <CoveragePageCrashoutPane
              matterId={matterId}
              coverages={coverages}
            />
          ) : (
            <CoverageContent
              isLoading={isLoading}
              error={!!error}
              coverages={coverages}
              selectionMode={selectionMode}
              matterId={matterId}
              filterCategory={filterCategory}
              onFilterChange={setFilterCategory}
              onCoverageSelect={handleCoverageSelect}
              onCreateCoverage={handleCreateCoverage}
              onUpdate={refetch}
              onRefetch={refetch}
              coverageCreationLocked={matterDetails?.coverage_creation_locked}
            />
          )}
        </div>
      </div>

      <ReviewGroupCoveragesModal
        isOpen={isManageSharedOpen}
        onClose={() => setIsManageSharedOpen(false)}
        matterId={matterId}
      />
    </div>
  );
};
